// src/components/Navigation/DesktopSidebar.js
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Collapse,
  Toolbar,
  useTheme,
  useMediaQuery,
  Box,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { toast } from 'react-toastify';

// Import icons from Material-UI
import InsightsIcon from '@mui/icons-material/Insights';
import CreateIcon from '@mui/icons-material/Create';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MicIcon from '@mui/icons-material/Mic';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Import navigation config
import { NAVIGATION_CONFIG } from '../../config/navigationConfig';

// Import for audio and upload functionality
import { useEntityContext } from '../EntityContextSwitch';
import { useUpload } from '../../contexts/UploadContext';
import { useRecorder } from '../../contexts/RecorderContext';

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 64;

/**
 * Desktop Sidebar Navigation
 *
 * Displays a permanent left sidebar navigation on desktop devices with:
 * - Collapsible width (280px expanded, 64px collapsed)
 * - Expandable Capture section with 3 subitems
 * - Active route highlighting with left border
 * - Smooth transitions
 * - Persistent state in localStorage
 *
 * Does NOT render on mobile devices
 */
// eslint-disable-next-line max-lines-per-function
function DesktopSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentEntity } = useEntityContext();
  const { triggerUpload } = useUpload();
  const { startRecording, isRecording, toggleExpanded } = useRecorder();

  // Sidebar collapsed state (persisted)
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Capture section expanded state (persisted)
  const [captureExpanded, setCaptureExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebar_capture_expanded');
    return saved === 'true';
  });

  // Icon mapping - matches approved icons from navigationConfig
  const iconMap = {
    'InsightsIcon': InsightsIcon,
    'CreateIcon': CreateIcon,
    'SmartToyIcon': SmartToyIcon,
    'AccountTreeIcon': AccountTreeIcon,
    'MicIcon': MicIcon,
    'UploadFileIcon': UploadFileIcon,
    'MenuBookIcon': MenuBookIcon,
    'VolunteerActivismIcon': VolunteerActivismIcon,
  };

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', collapsed);
  }, [collapsed]);

  // Persist capture expanded state
  useEffect(() => {
    localStorage.setItem('sidebar_capture_expanded', captureExpanded);
  }, [captureExpanded]);

  // Don't render on mobile devices
  if (isMobile) {
    return null;
  }

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const toggleCaptureExpanded = () => {
    setCaptureExpanded(!captureExpanded);
  };

  const handleNavigation = async (route, optionId) => {
    // Special handling for Audio - use the RecorderContext
    if (optionId === 'audio') {
      if (isRecording) {
        // If already recording, expand the floating recorder
        toggleExpanded();
      } else {
        // Start a new recording
        if (!currentEntity?.id) {
          toast.error('Please select an entity first');
          return;
        }
        await startRecording(currentEntity.id, currentEntity.name);
      }
      return;
    }

    // For Upload, trigger global file upload (no navigation)
    if (optionId === 'upload') {
      setTimeout(() => {
        triggerUpload();
      }, 100);
      return;
    }

    // For other options, navigate normally
    navigate(route);
  };

  const isActiveRoute = (route) => {
    return location.pathname === route;
  };

  const drawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: 200,
          }),
          overflowX: 'hidden',
        },
      }}
    >
      {/* Toolbar spacer - matches AppBar height */}
      <Toolbar />

      {/* Navigation items */}
      <List sx={{ flex: 1 }}>
        {/* eslint-disable-next-line max-lines-per-function */}
        {NAVIGATION_CONFIG.primary.map((item) => {
          const IconComponent = iconMap[item.icon];
          const isActive = isActiveRoute(item.route);
          const hasSubitems = item.subitems && item.subitems.length > 0;

          return (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (hasSubitems) {
                      // When collapsed, navigate to default route instead of toggling
                      if (collapsed) {
                        // Navigate to first subitem route (notebook for Capture)
                        const defaultRoute = item.subitems[0]?.route || item.route;
                        handleNavigation(defaultRoute);
                      } else {
                        // When expanded, toggle the collapse
                        toggleCaptureExpanded();
                      }
                    } else {
                      handleNavigation(item.route);
                    }
                  }}
                  sx={{
                    minHeight: 48,
                    justifyContent: collapsed ? 'center' : 'initial',
                    px: 2.5,
                    borderLeft: isActive ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                    backgroundColor: isActive ? 'rgba(201, 160, 220, 0.08)' : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive
                        ? 'rgba(201, 160, 220, 0.12)'
                        : 'rgba(255, 255, 255, 0.08)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: collapsed ? 0 : 3,
                      justifyContent: 'center',
                      color: isActive ? theme.palette.primary.light : theme.palette.primary.light,
                    }}
                  >
                    <IconComponent />
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText
                        primary={item.label}
                        sx={{
                          opacity: collapsed ? 0 : 1,
                          color: '#ffffff',
                        }}
                      />
                      {hasSubitems && (
                        captureExpanded ? <ExpandLessIcon sx={{ color: '#ffffff' }} /> : <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                      )}
                    </>
                  )}
                </ListItemButton>
              </ListItem>

              {/* Render subitems if exists (Capture section) */}
              {hasSubitems && !collapsed && (
                <Collapse in={captureExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subitems.map((subitem) => {
                      const SubIconComponent = iconMap[subitem.icon];
                      const isSubActive = isActiveRoute(subitem.route);

                      return (
                        <ListItem key={subitem.id} disablePadding>
                          <ListItemButton
                            onClick={() => handleNavigation(subitem.route, subitem.id)}
                            sx={{
                              pl: 4,
                              minHeight: 44,
                              borderLeft: isSubActive ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                              backgroundColor: isSubActive ? 'rgba(201, 160, 220, 0.08)' : 'transparent',
                              '&:hover': {
                                backgroundColor: isSubActive
                                  ? 'rgba(201, 160, 220, 0.12)'
                                  : 'rgba(255, 255, 255, 0.08)',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: 3,
                                justifyContent: 'center',
                                color: isSubActive ? theme.palette.primary.light : theme.palette.primary.light,
                              }}
                            >
                              <SubIconComponent fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={subitem.label}
                              sx={{ color: '#ffffff' }}
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Divider */}
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* Collapse toggle button at bottom */}
      <Box sx={{ p: 1 }}>
        <IconButton
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand navigation sidebar' : 'Collapse navigation sidebar'}
          sx={{
            width: '100%',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
    </Drawer>
  );
}

export default DesktopSidebar;
